import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLangContext } from "context/Lang";
import { AsyncProcess } from "services/AsyncProcess";
import { routes } from "routes";
import { Loader } from "components/Loader/Loader";
import { motion } from "framer-motion";
import { Map, ExternalLink, ShieldCheck, MapPin } from "lucide-react";
import { Button } from "components/Button/Button";
import { StatusLabel } from "components/status-label";

export const AssignedMaps = () => {
    const { gW } = useLangContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [assignedMapEditors, setAssignedMapEditors] = useState<MapEditorType[]>([]);

    const fetchMyMapAssignments = () => {
        setLoading(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.maps}/my`,
            setError: () => setLoading(false),
            effectProcess: [(res) => {
                if (res?.success !== false) {
                    setAssignedMapEditors(res?.data || []);
                }
                setLoading(false);
            }]
        });
    };

    useEffect(() => {
        fetchMyMapAssignments();
    }, []);

    if (loading) return <div className="flex justify-center p-20"><Loader /></div>;

    return (
        <motion.div 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }}
            className="p-6"
        >
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-primary-800">{gW("assigned-maps")}</h1>
                    <p className="text-primary-500">{gW("manage-your-editor-permissions")}</p>
                </div>
                <div className="bg-primary-100 p-2 rounded-lg">
                    <ShieldCheck className="text-primary-600" size={24} />
                </div>
            </div>

            {assignedMapEditors.length === 0 ? (
                <div className="text-center py-20 bg-primary-50 rounded-2xl border-2 border-dashed border-primary-200">
                    <Map className="mx-auto text-primary-300 mb-4" size={48} />
                    <p className="text-primary-500 italic">{gW("no-assigned-maps-found")}</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {assignedMapEditors.map((item) => (
                        <div 
                            key={item.id} 
                            className="bg-white rounded-2xl border border-primary-100 p-5 shadow-sm hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="bg-primary-50 p-3 rounded-xl">
                                    <Map className="text-primary-600" size={24} />
                                </div>
                                
                                <StatusLabel status={item?.threeMap?.status} />
                            </div>

                            <h3 className="text-lg font-bold text-primary-800 mb-1">
                                {item.threeMap?.title}
                            </h3>
                            <p className="text-sm text-primary-500 flex items-center gap-1 mb-6 line-clamp-2 h-10">
                                {item.threeMap?.description || gW("no-description")}
                            </p>

                            <Button 
                                className="w-full justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white"
                                onClick={() => navigate(`${routes.mapEditor}?mapEditorId=${item.id}`)}
                            >
                                <ExternalLink size={18} />
                                {gW("open-editor")}
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </motion.div>
    );
};